import React, { useMemo, useRef, useState } from "react";
import { Card, Button, Form, Alert, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";

import { State } from "../interfaces/types";
import authHeader from "../services/auth-header";
import { logout, setUserConditions } from "../slices/auth";
import { AppDispatch } from "../store";
import { useAppDispatch } from "../hooks";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const TermsAndConditions: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useAppDispatch();
  const { user: currentUser } = useSelector((state: State) => state.auth);

  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  // 🔒 Candado anti doble click / doble ejecución
  const inFlightRef = useRef(false);

  const hasConditionsField = useMemo(() => {
    return !!currentUser && Object.prototype.hasOwnProperty.call(currentUser, "conditions");
  }, [currentUser]);

  // --- Guards de navegación ---
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!hasConditionsField) return <Navigate to="/home" replace />;
  if (currentUser.conditions === true) return <Navigate to="/home" replace />;

  const handleCancel = async () => {
    await dispatch(logout());
    navigate("/login", { replace: true });
  };

  const handleAccept = async () => {
    // 🚫 Evita doble ejecución inmediata
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    setError("");
    setLoading(true);

    try {
      // 1) Actualizar condiciones en backend
      await axios.patch(
        `${API_URL}/clients/${currentUser.id}/accept-conditions`,
        {},
        { headers: authHeader() }
      );

      // 2) Actualizar Redux + localStorage
      dispatch(setUserConditions(true));

      // 3) Flujo de reset de contraseña (firstLogin)
      //const username = currentUser.username;
      //const response = await dispatch(forgotPasswordUser({ username })).unwrap();
      //const token = response.token;

      //const isNumeric = /^[0-9]+$/.test(username);

      navigate('/home');
    } catch (e: any) {
      console.error(e);
      setError(
        e?.response?.data?.message ||
          "No se pudo guardar la aceptación de términos. Intente nuevamente."
      );
    } finally {
      setLoading(false);
      inFlightRef.current = false; // 🔓 libera candado
    }
  };

  return (
    <div
      className="w-100 d-flex justify-content-center align-items-center"
      style={{ minHeight: "calc(100vh - 80px)", padding: 16 }}
    >
      <Card
        className="shadow"
        style={{
          maxWidth: 900,
          width: "100%",
          borderRadius: 16,
          overflow: "hidden",
          border: "none",
        }}
      >
        <div
          style={{
            background: "#3C3B2A",
            padding: "28px 22px",
            textAlign: "center",
            color: "white",
          }}
        >
          <div style={{ letterSpacing: 2, fontSize: 44, fontWeight: 800, lineHeight: 1.05 }}>
            GIMNASIO WEBB
          </div>
          <div style={{ letterSpacing: 2, fontSize: 26, fontWeight: 300, marginTop: 8 }}>
            TÉRMINOS Y CONDICIONES
          </div>
        </div>

        <Card.Body style={{ padding: "22px" }}>
          {error && <Alert variant="danger">{error}</Alert>}

          <div style={{ fontSize: 18, color: "#2f2f2f" }}>
            <ol style={{ paddingLeft: 20 }}>
              <li style={{ paddingLeft: 20 }}>
                Las membresías funcionan como suscripción continua. Debe notificarse cualquier
                ausencia para evitar cargos acumulados.
              </li>

              <li style={{ paddingLeft: 20 }}>
                El entrenamiento es semi personalizado y siempre supervisado por instructores.
                No se permite entrenar sin guía.
              </li>

              <li style={{ paddingLeft: 20 }}>
                La matrícula se paga una única vez al ingresar como nuevo usuario. Si el usuario
                permanece inactivo por más de un año, deberá volver a pagar la matrícula para
                reactivarse.
              </li>

              <li style={{ paddingLeft: 20 }}>
                Los beneficios de los paquetes familiares (de 2, 3 o más personas) se aplican únicamente si 
                pertenecen al mismo núcleo familiar y económico. Estos paquetes deben cancelarse en la misma 
                fecha acordada de pago. Si uno de los miembros del paquete familiar está moroso o inactivo, el
                beneficio quedará sin efecto y automáticamente se modificará la modalidad de todos los miembros
                del paquete a la correspondiente.
              </li>

              <li style={{ paddingLeft: 20 }}>
                Modalidades de suscripción disponibles:
                  <ul>
                    <li>Individual.</li>
                    <li>Familiar#1 - 2 personas.</li>
                    <li>Familiar#2 - 3 o más personas.</li>
                    <li>Modalidad con clases incluidas.</li>
                  </ul>
              </li>

              <li style={{ paddingLeft: 20 }} value={6}>
                El pago debe realizarse únicamente por tarjeta, transferencia o SINPE móvil. Se
                aplicará un recargo del 5% por morosidad después de 5 días.
              </li>

              <li style={{ paddingLeft: 20 }} value={7}>
                El respeto, la limpieza y el uso adecuado del equipo son indispensables.
              </li>
            </ol>
          </div>

          <hr />

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="acceptTerms"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              label="He leído y acepto los términos y condiciones del Gimnasio Webb."
            />
          </Form.Group>

          <div className="d-flex flex-column flex-md-row gap-2 justify-content-end">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={handleCancel}
              disabled={loading}
              style={{ minWidth: 140 }}
            >
              Cancelar
            </Button>

            <Button
              type="button"
              variant="success"
              onClick={handleAccept}
              disabled={!accepted || loading}
              style={{ minWidth: 140 }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Guardando...
                </>
              ) : (
                "Aceptar"
              )}
            </Button>
          </div>

          <div className="mt-3" style={{ fontSize: 13, color: "#6b6b6b" }}>
            * Si no aceptás los términos, se cerrará tu sesión.
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TermsAndConditions;
